import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type WheelEvent } from 'react';
import { compileCue, cueOccurrences } from '../../av/cues';
import { lowerBound, type ExtractedEvents, type MusicEvent } from '../../av/events';
import { mappingLength, mappingTriggers } from '../../av/reactions';
import type { AVSessionController } from '../../av/sessionController';
import { TICKS_PER_BAR, TICKS_PER_BEAT, TICKS_PER_STEP, formatPosition, secondsToTicks, ticksToSeconds } from '../../av/time';
import * as av from '../../domain/avOps';
import { NOTE_EVENT_CATEGORIES, type AvSession, type EventCategory, type Project } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import type { SyncSelectionStore } from '../../state/syncSelection';
import type { EditFn } from '../scene/useSceneEdit';
import { Button } from '../ui';
import { snapTicks, type Snap } from './snap';

/**
 * Unified audio-visual timeline (Phase 4A), drawn on two canvases (static content and a
 * playhead layer) so the DOM stays bounded however many events a session has; only events
 * inside the visible window are drawn (binary search). Lanes: ruler (bars, beats, sections,
 * loop), one lane per extracted instrument, manual markers, one lane per reaction (its
 * trigger envelopes), motion cues, and the animation (permanent keyframes).
 */
const LABEL_W = 92;
const RULER_H = 24;
const LANE_H = 16;
const LANE_GAP = 2;
const MIN_PX_PER_SEC = 4;
const MAX_PX_PER_SEC = 800;

type Lane =
  | { kind: 'category'; key: string; label: string; category: EventCategory }
  | { kind: 'markers'; key: string; label: string }
  | { kind: 'mapping'; key: string; label: string; mappingId: string }
  | { kind: 'cues'; key: string; label: string }
  | { kind: 'animation'; key: string; label: string };

const CATEGORY_COLORS: Partial<Record<EventCategory, string>> = {
  kick: '#f97316',
  snare: '#38bdf8',
  clap: '#22d3ee',
  hats: '#a3e635',
  perc: '#facc15',
  cowbell: '#fbbf24',
  bass808: '#f43f5e',
  lead: '#c084fc',
  pad: '#818cf8',
  manual: '#f472b6',
};
const SCENE_REACTION_COLOR = '#e4e4e7';
const OBJECT_REACTION_COLOR = '#a78bfa';


export function SyncTimeline({
  project,
  session,
  events,
  durationSec,
  sync,
  selection,
  edit,
}: {
  project: Project;
  session: AvSession;
  events: ExtractedEvents;
  durationSec: number;
  sync: AVSessionController;
  selection: SyncSelectionStore;
  edit: EditFn;
}) {
  const bpm = project.settings.bpm;
  const sel = useObservable(selection);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const staticRef = useRef<HTMLCanvasElement | null>(null);
  const headRef = useRef<HTMLCanvasElement | null>(null);
  const [width, setWidth] = useState(800);
  const [pxPerSec, setPxPerSec] = useState<number | null>(null);
  const [viewStart, setViewStart] = useState(0);
  const [snap, setSnap] = useState<Snap>('step');
  const drag = useRef<{ markerId: string; tick: number; moved: boolean } | null>(null);
  const [dragTick, setDragTick] = useState<number | null>(null);
  const scene = project.scenes.find((s) => s.id === session.sceneId)!;
  const animation = session.animationId ? project.animations.find((a) => a.id === session.animationId) : undefined;

  const plotW = Math.max(50, width - LABEL_W);
  const fitPx = Math.min(MAX_PX_PER_SEC, Math.max(MIN_PX_PER_SEC, plotW / Math.max(1, durationSec)));
  const pps = pxPerSec ?? fitPx;
  const visibleSec = plotW / pps;
  const maxStart = Math.max(0, durationSec - visibleSec);
  const start = Math.min(viewStart, maxStart);

  const lanes = useMemo<Lane[]>(() => {
    const out: Lane[] = [];
    for (const c of NOTE_EVENT_CATEGORIES) if (events.byCategory.get(c)?.length) out.push({ kind: 'category', key: `c-${c}`, label: av.CATEGORY_LABELS[c], category: c });
    out.push({ kind: 'markers', key: 'markers', label: 'Markers' });
    for (const m of session.mappings) out.push({ kind: 'mapping', key: `m-${m.id}`, label: m.name, mappingId: m.id });
    if (session.motionCues.length) out.push({ kind: 'cues', key: 'cues', label: 'Motion cues' });
    out.push({ kind: 'animation', key: 'animation', label: 'Keyframes' });
    return out;
  }, [events, session.mappings, session.motionCues.length]);
  const height = RULER_H + lanes.length * (LANE_H + LANE_GAP) + 4;
  const laneTop = (i: number) => RULER_H + i * (LANE_H + LANE_GAP) + 2;

  const xOf = useCallback((sec: number) => LABEL_W + (sec - start) * pps, [start, pps]);
  const secOf = useCallback((x: number) => start + (x - LABEL_W) / pps, [start, pps]);

  // Size follows the container.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => setWidth(Math.max(200, Math.floor(entries[0]!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- Static layer.
  useEffect(() => {
    const canvas = staticRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = Math.min(2, globalThis.devicePixelRatio || 1);
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#0c0c0f';
    ctx.fillRect(0, 0, width, height);
    const end = start + visibleSec;
    const t0 = Math.max(0, Math.floor(secondsToTicks(start, bpm)));
    const t1 = Math.ceil(secondsToTicks(end, bpm));
    ctx.save();
    ctx.beginPath();
    ctx.rect(LABEL_W, 0, plotW, height);
    ctx.clip();
    // Sections.
    for (const s of events.sections) {
      const a = xOf(ticksToSeconds(s.startStep * TICKS_PER_STEP, bpm));
      const b = xOf(ticksToSeconds(s.endStep * TICKS_PER_STEP, bpm));
      if (b < LABEL_W || a > width) continue;
      ctx.fillStyle = s.kind === 'drop' ? '#3b0764' : s.kind === 'buildup' ? '#422006' : '#18181b';
      ctx.fillRect(a, 0, b - a, 11);
      ctx.fillStyle = '#d4d4d8';
      ctx.font = '9px system-ui, sans-serif';
      ctx.fillText(s.name, a + 3, 9, Math.max(0, b - a - 4));
    }
    // Loop region.
    if (session.loop.enabled) {
      const a = xOf(ticksToSeconds(session.loop.startTick, bpm));
      const b = xOf(ticksToSeconds(session.loop.endTick, bpm));
      ctx.fillStyle = 'rgba(34,211,238,0.08)';
      ctx.fillRect(a, 11, b - a, height - 11);
      ctx.fillStyle = '#22d3ee';
      ctx.fillRect(a, 11, b - a, 2);
    }
    // Bars and beats.
    const beatPx = ticksToSeconds(TICKS_PER_BEAT, bpm) * pps;
    const barEvery = beatPx * 4 < 28 ? Math.ceil(28 / (beatPx * 4)) : 1;
    for (let t = Math.floor(t0 / TICKS_PER_BEAT) * TICKS_PER_BEAT; t <= Math.min(t1, events.totalTicks); t += TICKS_PER_BEAT) {
      const x = Math.round(xOf(ticksToSeconds(t, bpm))) + 0.5;
      const bar = t % TICKS_PER_BAR === 0;
      if (!bar && beatPx < 5) continue;
      ctx.strokeStyle = bar ? '#3f3f46' : '#1f1f23';
      ctx.beginPath();
      ctx.moveTo(x, bar ? 12 : 18);
      ctx.lineTo(x, height);
      ctx.stroke();
      const barIndex = t / TICKS_PER_BAR;
      if (bar && barIndex % barEvery === 0) {
        ctx.fillStyle = '#a1a1aa';
        ctx.font = '9px ui-monospace, monospace';
        ctx.fillText(String(barIndex + 1), x + 2, 22);
      }
    }
    // Lanes.
    const inView = (list: readonly MusicEvent[]) => {
      const i = lowerBound(list, t0 - TICKS_PER_BEAT);
      const out: MusicEvent[] = [];
      for (let j = i; j < list.length && list[j]!.tick <= t1; j++) out.push(list[j]!);
      return out;
    };
    lanes.forEach((lane, i) => {
      const y = laneTop(i);
      ctx.fillStyle = i % 2 ? '#111114' : '#0e0e11';
      ctx.fillRect(LABEL_W, y, plotW, LANE_H);
      if (lane.kind === 'category' || lane.kind === 'markers') {
        const list = events.byCategory.get(lane.kind === 'markers' ? 'manual' : lane.category) ?? [];
        const color = CATEGORY_COLORS[lane.kind === 'markers' ? 'manual' : lane.category] ?? '#a1a1aa';
        for (const e of inView(list)) {
          const dragged = lane.kind === 'markers' && drag.current && e.source.kind === 'marker' && e.source.markerId === drag.current.markerId && dragTick !== null;
          const x = xOf(ticksToSeconds(dragged ? dragTick! : e.tick, bpm));
          const h = lane.kind === 'markers' ? LANE_H - 2 : Math.max(3, (LANE_H - 2) * e.velocity);
          ctx.fillStyle = e.id === sel.eventId ? '#ffffff' : color;
          if (lane.kind === 'markers') {
            ctx.beginPath();
            ctx.moveTo(x, y + 1);
            ctx.lineTo(x + 5, y + LANE_H / 2);
            ctx.lineTo(x, y + LANE_H - 1);
            ctx.lineTo(x - 5, y + LANE_H / 2);
            ctx.fill();
            if (e.source.kind === 'marker' && e.source.label) {
              ctx.font = '9px system-ui, sans-serif';
              ctx.fillText(e.source.label, x + 7, y + LANE_H - 4);
            }
          } else ctx.fillRect(Math.round(x), y + LANE_H - 1 - h, Math.max(1.5, Math.min(4, pps * 0.01)), h);
        }
      } else if (lane.kind === 'mapping') {
        const m = session.mappings.find((x) => x.id === lane.mappingId);
        if (!m) return;
        const len = mappingLength(m);
        const color = m.target.kind === 'scene' ? SCENE_REACTION_COLOR : OBJECT_REACTION_COLOR;
        const triggers = mappingTriggers(m, events);
        let j = 0;
        // First trigger whose envelope reaches the view.
        let lo = 0;
        let hi = triggers.length;
        while (lo < hi) {
          const mid = (lo + hi) >> 1;
          if (triggers[mid]!.seconds + len < start) lo = mid + 1;
          else hi = mid;
        }
        for (j = lo; j < triggers.length && triggers[j]!.seconds <= end; j++) {
          const t = triggers[j]!;
          const a = xOf(t.seconds);
          const w = Math.max(1.5, len * pps);
          ctx.globalAlpha = (m.enabled ? 0.35 + 0.6 * t.velocity : 0.15) * (lane.mappingId === sel.mappingId ? 1 : 0.8);
          ctx.fillStyle = color;
          ctx.fillRect(a, y + 2, w, LANE_H - 4);
          ctx.globalAlpha = 1;
          ctx.fillRect(a, y + 1, 1.5, LANE_H - 2);
        }
        if (lane.mappingId === sel.mappingId) {
          ctx.strokeStyle = '#a78bfa';
          ctx.strokeRect(LABEL_W + 0.5, y + 0.5, plotW - 1, LANE_H - 1);
        }
      } else if (lane.kind === 'cues') {
        for (const cue of session.motionCues) {
          const c = compileCue(cue, scene, project.characters, bpm);
          for (const tick of cueOccurrences(cue)) {
            const a = xOf(ticksToSeconds(tick, bpm));
            if (a > width) break;
            ctx.fillStyle = cue.id === sel.cueId ? '#fde68a' : c.error ? '#7f1d1d' : '#f59e0b';
            ctx.globalAlpha = 0.7;
            ctx.fillRect(a, y + 2, Math.max(2, c.durationSec * pps), LANE_H - 4);
            ctx.globalAlpha = 1;
          }
        }
      } else if (lane.kind === 'animation' && animation) {
        const a0 = ticksToSeconds(session.animationStartTick, bpm);
        const a = xOf(a0);
        const b = xOf(a0 + animation.durationFrames / animation.fps);
        ctx.fillStyle = '#1e3a8a';
        ctx.fillRect(a, y + 3, b - a, LANE_H - 6);
        ctx.fillStyle = '#93c5fd';
        const frames = new Set<number>();
        for (const tr of animation.tracks) for (const k of tr.keyframes) frames.add(k.frame);
        for (const f of frames) {
          const x = xOf(a0 + f / animation.fps);
          if (x < LABEL_W - 4 || x > width) continue;
          ctx.fillRect(x - 1, y + 4, 2, LANE_H - 8);
        }
      }
    });
    // End of the session.
    const ex = xOf(durationSec);
    ctx.fillStyle = 'rgba(9,9,11,0.7)';
    ctx.fillRect(ex, 0, width - ex, height);
    ctx.restore();
    // Labels.
    ctx.fillStyle = '#0c0c0f';
    ctx.fillRect(0, 0, LABEL_W, height);
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillStyle = '#71717a';
    ctx.fillText('bar', 6, 20);
    lanes.forEach((lane, i) => {
      const y = laneTop(i);
      const selectedLane = lane.kind === 'mapping' && lane.mappingId === sel.mappingId;
      ctx.fillStyle = selectedLane ? '#ddd6fe' : lane.kind === 'mapping' ? '#c4b5fd' : lane.kind === 'animation' ? '#93c5fd' : '#a1a1aa';
      ctx.fillText(lane.label, 6, y + LANE_H - 4, LABEL_W - 10);
    });
  }, [width, height, start, pps, plotW, visibleSec, bpm, events, session, lanes, sel, dragTick, durationSec, animation, scene, project.characters, xOf]);

  // ---- Playhead layer (redrawn per animation frame while playing).
  const drawHead = useCallback(() => {
    const canvas = headRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = Math.min(2, globalThis.devicePixelRatio || 1);
    if (canvas.width !== Math.floor(width * dpr) || canvas.height !== Math.floor(height * dpr)) {
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    const x = xOf(sync.clock.seconds());
    if (x < LABEL_W || x > width) return;
    ctx.fillStyle = '#f4f4f5';
    ctx.fillRect(Math.round(x) - 0.5, 0, 1.5, height);
    ctx.beginPath();
    ctx.moveTo(x - 5, 0);
    ctx.lineTo(x + 5, 0);
    ctx.lineTo(x, 7);
    ctx.fill();
  }, [width, height, xOf, sync]);

  const clockState = useObservable(sync.clock);
  useEffect(() => {
    drawHead();
    if (!clockState.playing) return;
    let raf = 0;
    const loop = () => {
      drawHead();
      // Follow the playhead: page the view when it leaves.
      const pos = sync.clock.seconds();
      if (pxPerSec !== null && (pos > start + visibleSec || pos < start)) setViewStart(Math.max(0, pos - visibleSec * 0.1));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [clockState, drawHead, sync, start, visibleSec, pxPerSec]);

  // ---- Interaction.
  const hit = (x: number, y: number) => {
    const laneIndex = Math.floor((y - RULER_H) / (LANE_H + LANE_GAP));
    const lane = y >= RULER_H ? lanes[laneIndex] : undefined;
    return { lane, sec: secOf(x) };
  };
  const nearest = (list: readonly MusicEvent[], sec: number, px = 6): MusicEvent | null => {
    const tick = secondsToTicks(sec, bpm);
    const i = lowerBound(list, tick);
    let best: MusicEvent | null = null;
    let bestD = Infinity;
    for (const j of [i - 1, i]) {
      const e = list[j];
      if (!e) continue;
      const d = Math.abs(e.seconds - sec) * pps;
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return bestD <= px ? best : null;
  };
  const seekTo = (sec: number) => void sync.seek(Math.max(0, Math.min(durationSec, sec)));

  const onPointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    if (x < LABEL_W) {
      const { lane } = hit(x, y);
      if (lane?.kind === 'mapping') selection.update({ mappingId: lane.mappingId });
      return;
    }
    const { lane, sec } = hit(x, y);
    if (lane?.kind === 'markers') {
      const e = nearest(events.byCategory.get('manual') ?? [], sec);
      if (e && e.source.kind === 'marker') {
        selection.update({ eventId: e.id });
        drag.current = { markerId: e.source.markerId, tick: e.tick, moved: false };
        event.currentTarget.setPointerCapture?.(event.pointerId);
        return;
      }
    }
    if (lane?.kind === 'category') {
      const e = nearest(events.byCategory.get(lane.category) ?? [], sec);
      if (e) {
        selection.update({ eventId: e.id });
        seekTo(e.seconds);
        return;
      }
    }
    if (lane?.kind === 'mapping') selection.update({ mappingId: lane.mappingId });
    seekTo(sec);
  };
  const onPointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    if (!d) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const tick = snapTicks(secondsToTicks(secOf(event.clientX - rect.left), bpm), event.altKey ? 'tick' : snap);
    if (tick !== d.tick) d.moved = true;
    setDragTick(tick);
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    const tick = dragTick;
    setDragTick(null);
    if (!d) return;
    if (d.moved && tick !== null && tick !== d.tick) edit('Move marker', (p) => av.moveMarker(p, session.id, d.markerId, tick));
    else seekTo(ticksToSeconds(d.tick, bpm));
  };
  const onWheel = (event: WheelEvent<HTMLCanvasElement>) => {
    if (event.ctrlKey || event.metaKey) {
      const rect = event.currentTarget.getBoundingClientRect();
      zoomAt(event.deltaY < 0 ? 1.25 : 0.8, secOf(event.clientX - rect.left));
    } else {
      const delta = (Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY) / pps;
      setViewStart(Math.max(0, Math.min(maxStart, start + delta)));
    }
  };
  const zoomAt = (factor: number, anchorSec = sync.clock.seconds()) => {
    const next = Math.min(MAX_PX_PER_SEC, Math.max(fitPx, pps * factor));
    const anchorX = (anchorSec - start) * pps;
    setPxPerSec(next);
    setViewStart(Math.max(0, anchorSec - anchorX / next));
  };
  const step = (ticks: number) => {
    const now = Math.round(secondsToTicks(sync.clock.seconds(), bpm));
    const target = Math.max(0, Math.round((now + ticks) / Math.abs(ticks)) * Math.abs(ticks));
    seekTo(ticksToSeconds(target, bpm));
  };
  const onKeyDown = (event: KeyboardEvent<HTMLCanvasElement>) => {
    const k = event.key;
    if (k === 'ArrowRight' || k === 'ArrowLeft') {
      event.preventDefault();
      step((k === 'ArrowRight' ? 1 : -1) * (event.shiftKey ? TICKS_PER_BAR : TICKS_PER_BEAT));
    } else if (k === 'Home') {
      event.preventDefault();
      seekTo(0);
    } else if (k === 'End') {
      event.preventDefault();
      seekTo(durationSec);
    } else if (k === ' ') {
      event.preventDefault();
      void sync.toggle();
    } else if (k === '+' || k === '=') zoomAt(1.5);
    else if (k === '-') zoomAt(1 / 1.5);
    else if (k.toLowerCase() === 'm') {
      const tick = snapTicks(secondsToTicks(sync.clock.seconds(), bpm), snap);
      edit('Add marker', (p) => av.addMarker(p, session.id, tick, `Marker ${session.markers.length + 1}`).project);
    } else if ((k === 'Delete' || k === 'Backspace') && sel.eventId?.startsWith('marker:')) {
      const markerId = sel.eventId.slice(7);
      edit('Remove marker', (p) => av.removeMarker(p, session.id, markerId));
      selection.update({ eventId: null });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-xs" role="toolbar" aria-label="Timeline view">
        <Button size="sm" onClick={() => zoomAt(1 / 1.5)} aria-label="Zoom out" data-testid="sync-zoom-out">−</Button>
        <Button size="sm" onClick={() => zoomAt(1.5)} aria-label="Zoom in" data-testid="sync-zoom-in">+</Button>
        <Button size="sm" onClick={() => (setPxPerSec(null), setViewStart(0))} data-testid="sync-zoom-fit">Fit</Button>
        <label className="flex items-center gap-1 text-zinc-400">
          Scroll
          <input type="range" min={0} max={Math.max(0, maxStart)} step={0.01} value={start} disabled={maxStart <= 0} onChange={(e) => setViewStart(Number(e.target.value))} aria-label="Scroll the timeline" className="w-32" />
        </label>
        <label className="flex items-center gap-1 text-zinc-400">
          Snap
          <select value={snap} onChange={(e) => setSnap(e.target.value as Snap)} className="rounded border border-zinc-700 bg-zinc-900 px-1 py-0.5 text-zinc-200" aria-label="Snap markers to">
            <option value="beat">beats</option>
            <option value="step">16ths</option>
            <option value="tick">off</option>
          </select>
        </label>
        <span className="font-mono text-zinc-400" data-testid="sync-view">
          {formatPosition(Math.round(secondsToTicks(start, bpm)))} – {formatPosition(Math.round(secondsToTicks(Math.min(durationSec, start + visibleSec), bpm)))} · {pps.toFixed(0)} px/s
        </span>
      </div>
      <div ref={wrapRef} className="relative w-full overflow-hidden rounded-lg border border-zinc-800" style={{ height }}>
        <canvas ref={staticRef} className="absolute inset-0" style={{ width, height }} aria-hidden="true" />
        <canvas
          ref={headRef}
          className="absolute inset-0 cursor-crosshair outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
          style={{ width, height }}
          tabIndex={0}
          role="slider"
          aria-label="Audio-visual timeline. Arrow keys move by a beat (Shift: a bar), Space plays or pauses, M adds a marker, + and − zoom."
          aria-valuemin={0}
          aria-valuemax={Math.round(durationSec * 1000) / 1000}
          aria-valuenow={Math.round(clockState.originSec * 1000) / 1000}
          aria-valuetext={`${formatPosition(Math.round(secondsToTicks(clockState.originSec, bpm)))} (${clockState.originSec.toFixed(2)} seconds)`}
          data-testid="sync-timeline"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={onWheel}
          onKeyDown={onKeyDown}
        />
      </div>
    </div>
  );
}
